"use strict";
// A maintenance contract over its years (#515): each area's programme of works, year by year
// (the Take Off's "Schedule of Work" row under every section, T106:AH106, and the Scope sheet that
// reads it), and the contract it adds up to. Pure.
//
//   each year, each area   Repaint = the area's repaint · Service = its servicing (maintenance.ts)
//                          No Work = nothing · Wash = its wash row (DD's rule; not in the template)
//   a year's cost          the sum of what every area does that year, by category   (the PA sheets)
//   contract sell          Σ areas: repaint sell × Repaint years + service sell × Service years (W103)
//   GP %                   over the whole contract (Overall Summary, Z29)
//
// A new contract starts every area on year 1 Repaint and every later year Service (the template's
// defaults, T106 and U106:AH106). The template lets every year of every area be changed, year 1
// included, from Repaint · Service · No Work (Formulas!A273:A275).
Object.defineProperty(exports, "__esModule", { value: true });
exports.defaultProgramme = exports.YEAR_WORK = void 0;
exports.programmeFor = programmeFor;
exports.washYearArea = washYearArea;
exports.priceContract = priceContract;
const maintenance_ts_1 = require("./maintenance.js");
exports.YEAR_WORK = ['repaint', 'service', 'none', 'wash'];
const zeroCosts = () => ({ labour: 0, materials: 0, hire: 0, prelims: 0 });
const sum = (items, value) => items.reduce((total, item) => total + value(item), 0);
const totalOf = (costs) => sum(maintenance_ts_1.CONTRACT_CATEGORIES, (c) => costs[c]);
const NOTHING = { sell: 0, cost: zeroCosts(), totalCost: 0, hours: 0, litres: 0 };
function price(sell, cost, hours, litres) {
    return { sell, cost, totalCost: totalOf(cost), hours, litres };
}
/** The template's default programme: year 1 Repaint, every later year Service. */
const defaultProgramme = (years) => Array.from({ length: years }, (_, i) => (i === 0 ? 'repaint' : 'service'));
exports.defaultProgramme = defaultProgramme;
/**
 * An area's programme for a `years`-long contract: its stored choices, each year it doesn't set taking the
 * default; choices for years beyond the term are ignored (the term can shrink after they were set).
 */
function programmeFor(stored, years) {
    return (0, exports.defaultProgramme)(years).map((fallback, i) => {
        const chosen = stored?.[i];
        return chosen && exports.YEAR_WORK.includes(chosen) ? chosen : fallback;
    });
}
/** One area's repaint cost by category: Take Off AA:AE for its section (= its share of the Repaint Breakdown). */
function repaintOf(priced, rates) {
    const markupOther = 1 + rates.markupOther;
    const labourHours = priced.hours;
    const paintCost = sum(priced.surfaces, (s) => (s.litres * s.materialRate) / (1 + rates.markupMaterials));
    const sundryPaintCost = priced.sundryLitres * (rates.paints[0]?.costPerLitre ?? 0);
    const cost = {
        labour: labourHours * (rates.labour.cost + rates.allowance.cost) +
            priced.overtimeHours * rates.overtime.cost +
            priced.foremanHours * rates.foreman.cost +
            priced.abseilHours * rates.abseil.cost,
        materials: paintCost + sundryPaintCost + priced.consumables / markupOther,
        hire: sum(priced.access, (a) => a.sell) / markupOther,
        prelims: priced.fuelLevy + sum(priced.otherTrades, (o) => o.sell) / markupOther,
    };
    return price(priced.sell, cost, priced.hours, priced.litres);
}
/**
 * The m² a Wash year washes (DD, 23 Oct 2026): the area's own wash m² when it has one, else its surfaces' total
 * (#510's default), so a Wash year never prices nothing because the repaint wasn't washed. A typed 0 is the
 * estimator's "no wash m²" and washes nothing, as in the repaint; only no figure at all falls back (PR 547 review).
 * × the building's count.
 */
function washYearArea(input) {
    const area = input.washArea !== undefined ? input.washArea : (input.surfacesWashArea ?? 0);
    return area * Math.max(1, input.buildingCount ?? 1);
}
/** A Wash year: that m² at the area's wash rate, on labour, as the repaint's wash row (Take Off K64, AB64; #510's wash hours). */
function washOf(input, rates) {
    const area = washYearArea(input);
    const washRate = input.washRate ?? rates.washRate;
    const hours = washRate > 0 ? area / washRate : 0;
    const cost = { ...zeroCosts(), labour: hours * (rates.labour.cost + rates.allowance.cost) };
    return { ...price(hours * (rates.labour.sell + rates.allowance.sell), cost, hours, 0), area };
}
/** A Wash year on an area that isn't External is No Work: wash is external only (#510/#515), whatever was stored. */
const onSide = (input, work) => (work === 'wash' && input.side !== undefined && input.side !== 'External' ? 'none' : work);
/** What one year of `work` costs and sells for in an area. */
function pick(works, work) {
    return work === 'none' ? NOTHING : works[work];
}
function addInto(total, add) {
    for (const c of maintenance_ts_1.CONTRACT_CATEGORIES)
        total[c] += add[c];
}
function yearOf(sections, index) {
    const works = sections.map((s) => pick(s, s.programme[index] ?? 'none'));
    const cost = zeroCosts();
    for (const w of works)
        addInto(cost, w.cost);
    return { number: index + 1, cost, totalCost: totalOf(cost), sell: sum(works, (w) => w.sell), hours: sum(works, (w) => w.hours), litres: sum(works, (w) => w.litres) };
}
function combined(parts) {
    const cost = zeroCosts();
    for (const p of parts)
        addInto(cost, p.cost);
    return price(sum(parts, (p) => p.sell), cost, sum(parts, (p) => p.hours), sum(parts, (p) => p.litres));
}
/**
 * A maintenance option priced in full. `option` is priceOption's result for `sections` (the repaint);
 * `programmes` holds each area's stored choices by area id (absent = the default programme).
 */
function priceContract(sections, option, rates, setup, programmes = {}) {
    const priced = option.sections.map((section, i) => {
        const detail = (0, maintenance_ts_1.serviceSection)(sections[i], section, rates, setup);
        const repaint = repaintOf(section, rates);
        const service = { ...price(detail.sell, detail.cost, detail.hours, detail.litres), detail };
        const wash = washOf(sections[i], rates);
        const programme = programmeFor(programmes[section.id], setup.years).map((work) => onSide(sections[i], work));
        const works = programme.map((work) => pick({ repaint, service, wash }, work));
        return { id: section.id, name: section.name, programme, repaint, service, wash, sell: sum(works, (w) => w.sell), totalCost: sum(works, (w) => w.totalCost) };
    });
    const byYear = Array.from({ length: setup.years }, (_, i) => yearOf(priced, i));
    const sell = sum(priced, (s) => s.sell);
    const cost = sum(byYear, (y) => y.totalCost);
    return {
        years: setup.years,
        sections: priced,
        byYear,
        repaint: combined(priced.map((s) => s.repaint)),
        service: combined(priced.map((s) => s.service)),
        sell,
        cost,
        gp: sell > 0 ? (sell - cost) / sell : 0,
    };
}
